import { forwardRef } from 'react'
import { DropdownMenu as DropdownMenuPrimitive } from 'radix-ui'
import { cn } from '@/lib/utils'
import { CheckIcon, ChevronRightIcon } from 'lucide-react'
import { SURFACE_MOTION_CLASS } from '@/components/ui/motion'
function DropdownMenu({ ...props }) {
  return <DropdownMenuPrimitive.Root data-slot="dropdown-menu" {...props} />
}
function DropdownMenuPortal({ ...props }) {
  return <DropdownMenuPrimitive.Portal data-slot="dropdown-menu-portal" {...props} />
}
const DropdownMenuTrigger = forwardRef(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Trigger
    ref={ref}
    data-slot="dropdown-menu-trigger"
    className={className}
    {...props}
  />
))
DropdownMenuTrigger.displayName = DropdownMenuPrimitive.Trigger.displayName
function DropdownMenuContent({ className, align = 'start', sideOffset = 4, children, ...props }) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        data-slot="dropdown-menu-content"
        sideOffset={sideOffset}
        align={align}
        className={cn(
          SURFACE_MOTION_CLASS,
          'ring-foreground/5 bg-popover text-popover-foreground min-w-40 rounded-md border border-border/40 p-1 shadow-md z-[999] max-h-(--radix-dropdown-menu-content-available-height) w-(--radix-dropdown-menu-trigger-width) max-w-[calc(100vw-1rem)] origin-(--radix-dropdown-menu-content-transform-origin) overflow-visible',
          className,
        )}
        {...props}
      >
        {children}
      </DropdownMenuPrimitive.Content>
    </DropdownMenuPrimitive.Portal>
  )
}
function DropdownMenuGroup({ ...props }) {
  return <DropdownMenuPrimitive.Group data-slot="dropdown-menu-group" {...props} />
}
function DropdownMenuItem({ className, inset, variant = 'default', children, ...props }) {
  return (
    <DropdownMenuPrimitive.Item
      data-slot="dropdown-menu-item"
      data-inset={inset}
      data-variant={variant}
      className={cn(
        "data-[variant=destructive]:text-destructive data-[variant=destructive]:*:[svg]:text-destructive gap-2 rounded-sm px-2 py-1 text-[13px] [&_svg:not([class*='size-'])]:size-3.5 group/dropdown-menu-item relative flex cursor-pointer items-center outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          'absolute inset-0 rounded-sm opacity-0 group-focus/dropdown-menu-item:opacity-100 transition-opacity duration-150 pointer-events-none',
          variant === 'destructive' ? 'bg-destructive/10 dark:bg-destructive/20' : 'bg-accent',
        )}
      />
      <span className="relative z-10 flex items-center gap-2.5 w-full">{children}</span>
    </DropdownMenuPrimitive.Item>
  )
}
function DropdownMenuCheckboxItem({ className, children, checked, ...props }) {
  return (
    <DropdownMenuPrimitive.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      className={cn(
        "gap-2 rounded-sm py-1.25 pr-8 pl-2 text-[13px] [&_svg:not([class*='size-'])]:size-3.5 relative flex cursor-pointer items-center outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 group/dropdown-menu-checkbox-item",
        className,
      )}
      checked={checked}
      {...props}
    >
      <div className="absolute inset-0 bg-accent rounded-sm opacity-0 group-focus/dropdown-menu-checkbox-item:opacity-100 transition-opacity duration-150 pointer-events-none" />
      <span
        className="pointer-events-none absolute right-2 flex items-center justify-center pointer-events-none z-10"
        data-slot="dropdown-menu-checkbox-item-indicator"
      >
        <DropdownMenuPrimitive.ItemIndicator>
          <CheckIcon />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      <span className="relative z-10 flex items-center gap-2.5 w-full">{children}</span>
    </DropdownMenuPrimitive.CheckboxItem>
  )
}
function DropdownMenuRadioGroup({ ...props }) {
  return <DropdownMenuPrimitive.RadioGroup data-slot="dropdown-menu-radio-group" {...props} />
}
function DropdownMenuRadioItem({ className, children, ...props }) {
  return (
    <DropdownMenuPrimitive.RadioItem
      data-slot="dropdown-menu-radio-item"
      className={cn(
        "gap-2 rounded-sm py-1.25 pr-8 pl-2 text-[13px] [&_svg:not([class*='size-'])]:size-3.5 relative flex cursor-pointer items-center outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 group/dropdown-menu-radio-item",
        className,
      )}
      {...props}
    >
      <div className="absolute inset-0 bg-accent rounded-sm opacity-0 group-focus/dropdown-menu-radio-item:opacity-100 transition-opacity duration-150 pointer-events-none" />
      <span
        className="pointer-events-none absolute right-2 flex items-center justify-center pointer-events-none z-10"
        data-slot="dropdown-menu-radio-item-indicator"
      >
        <DropdownMenuPrimitive.ItemIndicator>
          <CheckIcon />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      <span className="relative z-10 flex items-center gap-2.5 w-full">{children}</span>
    </DropdownMenuPrimitive.RadioItem>
  )
}
function DropdownMenuLabel({ className, inset, ...props }) {
  return (
    <DropdownMenuPrimitive.Label
      data-slot="dropdown-menu-label"
      data-inset={inset}
      className={cn('text-muted-foreground px-3 py-2.5 text-xs data-[inset]:pl-8', className)}
      {...props}
    />
  )
}
function DropdownMenuSeparator({ className, ...props }) {
  return (
    <DropdownMenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cn('bg-border/10 -mx-1 my-1 h-px', className)}
      {...props}
    />
  )
}
function DropdownMenuShortcut({ className, ...props }) {
  return (
    <div
      data-slot="dropdown-menu-shortcut"
      className={cn('ml-auto text-xs text-muted-foreground', className)}
      {...props}
    />
  )
}
function DropdownMenuSub({ ...props }) {
  return <DropdownMenuPrimitive.Sub data-slot="dropdown-menu-sub" {...props} />
}
function DropdownMenuSubTrigger({ className, inset, children, ...props }) {
  return (
    <DropdownMenuPrimitive.SubTrigger
      data-slot="dropdown-menu-sub-trigger"
      data-inset={inset}
      className={cn(
        "gap-2 rounded-sm px-2 py-1 text-[13px] [&_svg:not([class*='size-'])]:size-3.5 flex cursor-pointer items-center outline-hidden select-none data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 relative group/dropdown-menu-sub-trigger",
        className,
      )}
      {...props}
    >
      <div className="absolute inset-0 bg-accent rounded-sm opacity-0 group-focus/dropdown-menu-sub-trigger:opacity-100 data-[state=open]:opacity-100 transition-opacity duration-150 pointer-events-none" />
      <span className="relative z-10 flex items-center w-full">
        {children}
        <ChevronRightIcon className="ml-auto" />
      </span>
    </DropdownMenuPrimitive.SubTrigger>
  )
}
function DropdownMenuSubContent({ className, children, ...props }) {
  return (
    <DropdownMenuPrimitive.SubContent
      data-slot="dropdown-menu-sub-content"
      className={cn(
        SURFACE_MOTION_CLASS,
        'ring-foreground/5 bg-popover text-popover-foreground min-w-36 max-w-[calc(100vw-1rem)] rounded-md border border-border/40 p-1 shadow-md z-[999] origin-(--radix-dropdown-menu-content-transform-origin) overflow-visible',
        className,
      )}
      {...props}
    >
      {children}
    </DropdownMenuPrimitive.SubContent>
  )
}
export {
  DropdownMenu,
  DropdownMenuPortal,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
}
